import { useState, useEffect, useCallback } from 'react';
import { getValidToken } from '../utils/auth';

const API_URL = process.env.REACT_APP_API_URL || '';

const STATUS_COLOR = { Active: '#60a5fa', Success: '#22c55e', Failed: '#ef4444' };
const DIR_COLOR = { C: '#22c55e', P: '#ef4444' };
const SETUP_LABEL = { breakout: 'Breakout', bounce: 'Oversold Bounce', breakdown: 'Breakdown' };

function formatDate(unixMs) {
  if (!unixMs) return '—';
  return new Date(unixMs).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', timeZone: 'America/New_York',
  });
}

function openTradingView(symbol) {
  window.open(`https://www.tradingview.com/chart/?symbol=${symbol}&interval=D`, '_blank', 'noopener,noreferrer');
}

function SetupCard({ dir, setup }) {
  const label = dir === 'C' ? 'Bullish' : 'Bearish';
  if (!setup) {
    return (
      <div className="flex-1 rounded-xl p-4" style={{ backgroundColor: '#111827', border: '1px solid #1f2937' }}>
        <div className="text-xs font-semibold text-gray-500 mb-2">{label}</div>
        <div className="text-sm text-gray-600">No setup currently — waiting for a daily-bar breakout, bounce, or breakdown.</div>
      </div>
    );
  }
  return (
    <div className="flex-1 rounded-xl p-4" style={{ backgroundColor: '#111827', border: `1px solid ${DIR_COLOR[dir]}44` }}>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xs font-semibold" style={{ color: DIR_COLOR[dir] }}>{label}</span>
        <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold" style={{ backgroundColor: '#37415144', color: '#9ca3af' }}>
          {SETUP_LABEL[setup.setup_type] || setup.setup_type}
        </span>
        {setup.earnings_in_window && (
          <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold" style={{ backgroundColor: '#f59e0b22', color: '#f59e0b' }}>
            ⚠ Earnings in window
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs font-mono">
        <div className="text-gray-500">Entry <span className="text-gray-200">${setup.entry_level.toFixed(2)}</span></div>
        <div className="text-gray-500">Stop <span style={{ color: '#ef4444' }}>${setup.stop_level.toFixed(2)}</span></div>
        <div className="text-gray-500">Target 1 <span style={{ color: '#22c55e' }}>${setup.target1.toFixed(2)}</span></div>
        <div className="text-gray-500">Target 2 <span style={{ color: '#22c55e' }}>${setup.target2.toFixed(2)}</span></div>
      </div>
    </div>
  );
}

// NVDA swing options scanner (backend: handlers/nvda_swing.go). Daily-bar
// breakout/oversold-bounce/breakdown setups on NVDA itself; once the entry
// level triggers, a 5-30 DTE contract near 0.45 delta is picked. Unlike the
// SPX 0DTE widgets, Success/Failed track the STOCK's price (intraday target
// touch, daily-close stop) — not the option premium, matching how a real
// swing trader manages the position. No position sizing (shown, sized
// manually); resolution can take days to weeks, so there's no date picker —
// this shows the last 90 days of signals, active ones first.
export default function NVDASwingWidget() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const token = await getValidToken();
      const res = await fetch(`${API_URL}/api/nvda-swing`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const timer = setInterval(fetchData, 30000);
    return () => clearInterval(timer);
  }, [fetchData]);

  const signals = data?.signals || [];
  const counts = { Active: 0, Success: 0, Failed: 0 };
  for (const s of signals) if (counts[s.status] != null) counts[s.status]++;

  return (
    <div className="bg-gray-900 rounded-2xl p-5 mb-5">
      <div className="flex items-center gap-3 mb-1 flex-wrap">
        <span className="text-xs font-bold px-3 py-1.5 rounded border"
          style={{ backgroundColor: '#111827', color: '#e5e7eb', borderColor: '#374151' }}>
          NVDA Swing Scanner
        </span>
        {data && <span className="text-xs text-gray-500 font-mono">NVDA ${data.price?.toFixed(2)}</span>}
        <div className="flex-1" />
        {Object.entries(counts).map(([st, n]) => (
          <span key={st} className="text-xs font-semibold" style={{ color: STATUS_COLOR[st] }}>{st} {n}</span>
        ))}
      </div>
      <div className="text-xs text-gray-500 mb-3">
        Daily-bar SMA20/50 breakout, oversold bounce, or breakdown setups · 5–30 DTE contract near 0.45 delta at trigger · stop/target tracked on NVDA's own price
      </div>
      {error && <div className="text-red-400 text-sm mb-3">Failed to load: {error}</div>}
      {loading ? (
        <div className="text-gray-500 text-sm text-center py-6">Loading…</div>
      ) : (
        <>
          <div className="flex gap-3 mb-4 flex-wrap">
            <SetupCard dir="C" setup={data?.setups?.C} />
            <SetupCard dir="P" setup={data?.setups?.P} />
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs" style={{ borderCollapse: 'collapse' }}>
              <thead>
                <tr className="text-gray-500 border-b" style={{ borderColor: '#1f2937' }}>
                  {['Contract', 'Dir', 'Setup', 'Entry Prem', 'Delta', 'DTE', 'Stop', 'T1', 'T2', 'High', 'Low', 'Added', 'Status'].map((h) => (
                    <th key={h} className="text-left py-2 pr-3 font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {signals.length === 0 ? (
                  <tr><td colSpan={13} className="py-4 text-center text-gray-600">No signals yet</td></tr>
                ) : signals.map((s, i) => (
                  <tr key={`${s.contract_symbol}-${s.added_at}-${i}`} onClick={() => openTradingView(s.symbol)}
                    className="border-b cursor-pointer hover:bg-gray-800/50" style={{ borderColor: '#1f2937' }}>
                    <td className="py-1.5 pr-3 font-semibold text-gray-200">{s.contract_symbol}</td>
                    <td className="py-1.5 pr-3 font-semibold" style={{ color: DIR_COLOR[s.direction] }}>{s.direction}</td>
                    <td className="py-1.5 pr-3 text-gray-400">{SETUP_LABEL[s.setup_type] || s.setup_type}</td>
                    <td className="py-1.5 pr-3 text-gray-300 font-mono">${s.entry_premium.toFixed(2)}</td>
                    <td className="py-1.5 pr-3 text-gray-400 font-mono">{s.delta.toFixed(2)}</td>
                    <td className="py-1.5 pr-3 text-gray-400 font-mono">{s.dte}</td>
                    <td className="py-1.5 pr-3 font-mono" style={{ color: '#ef4444' }}>${s.stop_level.toFixed(2)}</td>
                    <td className="py-1.5 pr-3 font-mono" style={{ color: '#22c55e' }}>${s.target1.toFixed(2)}</td>
                    <td className="py-1.5 pr-3 font-mono" style={{ color: '#22c55e' }}>${s.target2.toFixed(2)}</td>
                    <td className="py-1.5 pr-3 text-gray-400 font-mono">${s.high.toFixed(2)}</td>
                    <td className="py-1.5 pr-3 text-gray-400 font-mono">${s.low.toFixed(2)}</td>
                    <td className="py-1.5 pr-3 text-gray-400">{formatDate(s.added_at)}</td>
                    <td className="py-1.5 pr-3">
                      <span className="font-semibold px-1.5 py-0.5 rounded"
                        style={{ backgroundColor: (STATUS_COLOR[s.status] || '#6b7280') + '22', color: STATUS_COLOR[s.status] || '#6b7280' }}>
                        {s.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
